import { Location } from "history";
import { patientStore } from "../../../stores/PatientStore";
import { history } from "./history";
import { Routes } from "./Routes";

export type NavigationState = {
    /** True when the user entered via a report click; enables tab navigation and referrer handling. */
    reportNavigation?: boolean;
    /** Path to return to when using the back button (e.g. /welcome, /overview). Cleared only when navigating back. */
    from?: string;
    /** Checkup id used as anchor for the full history view (may differ from the report checkup). */
    historyAnchorCheckupId?: string;
    /** Checkup id of the report currently in context (for switching back from Verlauf or to edit). */
    reportCheckupId?: string;
    /** Whether the Befund tab is available (checkup released to patient). */
    reportAvailable?: boolean;
    /** Whether the Verlauf tab is available (more than one checkup with history). */
    historyAvailable?: boolean;
    /** Number of released checkups for the current patient (doctor context). */
    releasedCheckupCount?: number;
};

export const getNavigationState = (location: Location): NavigationState =>
    (location.state as NavigationState | undefined) ?? {};

export const getReferrerPath = (location: Location): string | undefined => getNavigationState(location).from;

/**
 * Resolves the checkup id for the report/edit tabs. The route param on /history/:id is the
 * history anchor and must not be used as a fallback for the report or edit view.
 */
export const resolveReportCheckupId = (
    navigationState: NavigationState,
    routeCheckupId: string | undefined,
    isReportView: boolean,
    isDoctorCheckup: boolean,
): string | undefined => {
    if (navigationState.reportCheckupId) {
        return navigationState.reportCheckupId;
    }

    if (isReportView || isDoctorCheckup) {
        return routeCheckupId;
    }

    return undefined;
};

export const resolveHistoryAnchorCheckupId = (
    navigationState: NavigationState,
    routeCheckupId: string | undefined,
): string => navigationState.historyAnchorCheckupId ?? routeCheckupId ?? "";

export const buildNavigationState = (
    location: Location,
    overrides: Partial<NavigationState> = {},
): NavigationState => {
    const current = getNavigationState(location);

    return {
        reportNavigation: overrides.reportNavigation ?? current.reportNavigation,
        from: overrides.from ?? current.from,
        historyAnchorCheckupId: overrides.historyAnchorCheckupId ?? current.historyAnchorCheckupId,
        reportCheckupId: overrides.reportCheckupId ?? current.reportCheckupId,
        reportAvailable: overrides.reportAvailable ?? current.reportAvailable,
        historyAvailable: overrides.historyAvailable ?? current.historyAvailable,
        releasedCheckupCount: overrides.releasedCheckupCount ?? current.releasedCheckupCount,
    };
};

export const navigateWithNavigationState = (
    path: string,
    location: Location,
    overrides: Partial<NavigationState> = {},
) => {
    history.push(path, buildNavigationState(location, overrides));
};

export const replaceWithNavigationState = (
    path: string,
    location: Location,
    overrides: Partial<NavigationState> = {},
) => {
    history.replace(path, buildNavigationState(location, overrides));
};

const navigateWithReportContext = (
    path: string,
    checkupId: string,
    location: Location,
    options?: {
        from?: string;
        historyAnchorCheckupId?: string;
        reportAvailable?: boolean;
        historyAvailable?: boolean;
        releasedCheckupCount?: number;
    },
) => {
    if (options?.releasedCheckupCount !== undefined) {
        patientStore.releasedCheckupCount = options.releasedCheckupCount;
    }

    navigateWithNavigationState(path, location, {
        reportNavigation: true,
        from: options?.from ?? getReferrerPath(location) ?? Routes.DOCTOR.OVERVIEW,
        reportCheckupId: checkupId,
        historyAnchorCheckupId: options?.historyAnchorCheckupId ?? checkupId,
        reportAvailable: options?.reportAvailable,
        historyAvailable: options?.historyAvailable,
        releasedCheckupCount: options?.releasedCheckupCount,
    });
};

export const navigateToReportWithContext = (
    checkupId: string,
    location: Location,
    options?: {
        from?: string;
        historyAnchorCheckupId?: string;
        historyAvailable?: boolean;
        releasedCheckupCount?: number;
    },
) => {
    navigateWithReportContext(Routes.CHECKUP_REPORT.replace(":checkupId", checkupId), checkupId, location, {
        ...options,
        reportAvailable: true,
    });
};

export const navigateToCheckupWithContext = (
    checkupId: string,
    location: Location,
    options?: {
        from?: string;
        historyAnchorCheckupId?: string;
        reportAvailable?: boolean;
        historyAvailable?: boolean;
        releasedCheckupCount?: number;
    },
) => {
    navigateWithReportContext(Routes.DOCTOR.CHECKUP.replace(":checkupId", checkupId), checkupId, location, options);
};

export const navigateToHistoryWithContext = (
    historyAnchorCheckupId: string,
    location: Location,
    options?: {
        from?: string;
        reportCheckupId?: string;
        reportAvailable?: boolean;
        historyAvailable?: boolean;
        releasedCheckupCount?: number;
    },
) => {
    if (options?.releasedCheckupCount !== undefined) {
        patientStore.releasedCheckupCount = options.releasedCheckupCount;
    }

    navigateWithNavigationState(Routes.DOCTOR.HISTORY.replace(":checkupId", historyAnchorCheckupId), location, {
        reportNavigation: true,
        from: options?.from ?? getReferrerPath(location) ?? Routes.DOCTOR.OVERVIEW,
        historyAnchorCheckupId,
        reportCheckupId: options?.reportCheckupId ?? historyAnchorCheckupId,
        reportAvailable: options?.reportAvailable ?? true,
        historyAvailable: options?.historyAvailable ?? true,
        releasedCheckupCount: options?.releasedCheckupCount,
    });
};
